<script lang="ts" setup>
import {
  computed,
  onMounted,
  ref,
  watch,
} from 'vue';

import Swal from 'sweetalert2';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { ElMessage } from 'element-plus';
import QrcodeVue from 'qrcode.vue';
import sharedOrg from '@/views/manage-users-and-organizations/shared';
import shared from '@/views/manage-content/media/shared';
import fileUpload from '@/shared/upload';

import StickerContent from './components/StickerContent.vue';
import DefaultSticker from '@/views/manage-content/media/add-sticker/components/DefaultSticker.vue';
import CampaignSticker from '@/views/manage-content/media/add-sticker/components/CampaignSticker.vue';
import TicketSticker from '@/views/manage-content/media/add-sticker/components/TicketSticker.vue';
import SweepstakesSticker from './components/SweepstakesSticker.vue';
import router from '@/router';
import BreadcrumbActions from '@/views/manage-content/add-item/BreadcrumbActions.vue';
import Insights from '@/views/manage-content/add-item/Insights.vue';
import { uploadDefaultStickerMap, uploadTicketStickerMap } from '@/shared/upload-sticker-model';
import { resizeAndConvertToPNG } from '@/shared/utility-functions';

const store = useStore();
const route = useRoute();
const isEditPage = ref(false);

const { uploadStatus, handleUploadRequest } = fileUpload('stickers', 'Sticker');

// Define interfaces first
interface TagData {
  value: string;
  label: string;
}
interface CauseData {
  value: string;
  label: string;
}

interface Winner {
  id: string;
  stickerId: string;
  entityId: string;
  participantName: string;
  participantAvatar: string;
  eligible: boolean;
  dateCreated: string;
  dateUpdated: string;
}

// Define ref variables
const winnerName = ref<string | null>(null);
const errors = ref<string[]>([]);
const winners = ref<Winner[] | null>(null);
const selectedWinners = ref<Winner[]>([]);
const clickCount = ref(0);
const isTextVisible = ref(false);
const isStickerSold = ref(false);
const stickerUrl = ref('');
const isStripeOnboarded = ref(false);
const isUploading = ref(false);

const {
  updateSticker,
  sendLoserEmails,
  getBullhornsAsTags,
  getcausesAsDropdown,
  getSticker,
  dateAndTime,
  drawWinner,
  getWinners,
  findStickerInsights,
} = shared();
const { updateOrgSticker, getOrganization } = sharedOrg();

const stickerType = ref('default'); // default, campaign, ticket, sweepstakes

const item = store.getters['sticker/GET_STICKER'];

const isAdmin = computed(() => store.getters['users/isAdmin']);
const isOrgAdmin = computed(() => store.getters['users/isOrgAdmin']);
const selectedOrganization = computed(() => store.getters['organizations/GET_SELECTED_ORGANIZATION']);
console.log('-- selectedOrganization: ',selectedOrganization.value)
const isSweepstakeDisabled = computed(() => {
  if (item.status === 'Active' || (item.status === 'Archived' && isStickerSold)) {
    return true;
  } return false;
});

// Helper functions defined before they are used
const getSweepstakesStickerProperties = (itemParam: any) => ({
  id: itemParam.id,
  title: itemParam.title,
  slug: itemParam.slug,
  description: itemParam.description,
  url: itemParam.url,
  tags: itemParam.tags,
  cost: itemParam.cost,
  campaignType: itemParam.campaignType,
  causes: itemParam.causes,
  maxQuantity: itemParam.maxQuantity,
  maxDonation: itemParam.maxDonation,
  donationMultiplier: itemParam.donationMultiplier,
  impact: itemParam.impact,
  startDate: itemParam.startDate,
  endDate: itemParam.endDate,
  waiverURL: itemParam.waiverURL,
  varients: null,
  quickCosts: [],
  glow: itemParam.glow ? {
    glowStartDate: itemParam.glow.glowStartDate,
    glowEndDate: itemParam.glow.glowEndDate,
    glowColor: itemParam.glow.glowColor,
    glowStartDateTimeZone: itemParam.glow.glowStartDateTimeZone,
    glowEndDateTimeZone: itemParam.glow.glowEndDateTimeZone,
  } : null,
  location: itemParam.location,
  isFeatured: itemParam.isFeatured,
  isCausesSearchAllowed: itemParam.isCausesSearchAllowed,
  confirmationEmail: itemParam.confirmationEmail,
  reminderEmail: itemParam.reminderEmail,
  hasOptions: itemParam.hasOptions,
  sweepstakesSticker: {
    aboutTheCause: itemParam.sweepstakesSticker.aboutTheCause,
    numberOfPrizesAndWinners: itemParam.sweepstakesSticker.numberOfPrizesAndWinners,
    prizes: itemParam.sweepstakesSticker.prizes,
    nonProfitBeneficiary: itemParam.sweepstakesSticker.nonProfitBeneficiary,
    drawingDateTime: itemParam.sweepstakesSticker.drawingDateTime,
    drawingDateTimeTimezone: itemParam.sweepstakesSticker.drawingDateTimeTimezone,
    experienceDateTime: itemParam.sweepstakesSticker.experienceDateTime,
    experienceDateTimeTimezone: itemParam.sweepstakesSticker.experienceDateTimeTimezone,
    approximateRetailValue: itemParam.sweepstakesSticker.approximateRetailValue,
    sweepstakesConfirmation: itemParam.sweepstakesSticker.sweepstakesConfirmation,
    sweepstakesWinnerMessage: itemParam.sweepstakesSticker.sweepstakesWinnerMessage,
    sweepstakesLoserMessage: itemParam.sweepstakesSticker.sweepstakesLoserMessage,
    checkoutPageLegal: itemParam.sweepstakesSticker.checkoutPageLegal,
    entries: itemParam.sweepstakesSticker.entries,
    entryCost: itemParam.sweepstakesSticker.entryCost,
  },
  startDateTimeZone: itemParam.startDateTimeZone || null,
  endDateTimeZone: itemParam.endDateTimeZone || null,
  eventDateTimeZone: itemParam.eventDateTimeZone || null,
});

const getStickerProperties = (itemParam: any) => ({
  id: itemParam.id,
  title: itemParam.title,
  slug: itemParam.slug,
  description: itemParam.description,
  url: itemParam.url,
  tags: itemParam.tags,
  cost: itemParam.cost,
  campaignType: itemParam.campaignType,
  causes: itemParam.causes,
  maxQuantity: itemParam.maxQuantity,
  maxDonation: itemParam.maxDonation,
  donationMultiplier: itemParam.donationMultiplier,
  impact: itemParam.impact,
  startDate: itemParam.startDate,
  endDate: itemParam.endDate,
  waiverURL: itemParam.waiverURL,
  location: itemParam.location,
  varients: {
    id: itemParam.id,
    cost: itemParam.cost,
    title: itemParam.title,
    sortOrder: itemParam.sortOrder,
    stickerId: itemParam.id,
    description: itemParam.description,
    isActive: itemParam.isActive,
    createdAt: itemParam.createdAt,
    updatedAt: itemParam.updatedAt,
  },
  glow: itemParam.glow,
  quickCosts: itemParam.quickCosts,
  sweepstakesSticker: null,
  startDateTimeZone: itemParam.startDateTimeZone || null,
  endDateTimeZone: itemParam.endDateTimeZone || null,
  eventDateTimeZone: itemParam.eventDateTimeZone || null,
});

const populateItem = async (itemParam: any) => {
  itemParam.tags = (typeof itemParam.tags === 'string' && itemParam.tags !== '') ? itemParam.tags.split(' |=| ') : [];
  itemParam.causes = (typeof itemParam.causes === 'string' && itemParam.causes !== '') ? itemParam.causes.split(' |=| ') : [];
  [itemParam.postDate, itemParam.postTime] = dateAndTime(itemParam.createdAt);
  itemParam.varients = itemParam.varients.filter((x: any) => x.isActive === true);

  itemParam.glow = {
    glowStartDate: itemParam.glow?.glowStartDate,
    glowEndDate: itemParam.glow?.glowEndDate,
    glowColor: itemParam.glow?.glowColor,
    glowStartDateTimeZone: itemParam.glow?.glowStartDateTimeZone,
    glowEndDateTimeZone: itemParam.glow?.glowEndDateTimeZone,
  };

  await store.dispatch('sticker/SET_STICKER', {
    endDate: itemParam.endDate,
    startDate: itemParam.startDate,
  });

  switch (itemParam.campaignType) {
    case 'Ticket':
      stickerType.value = 'ticket';
      break;
    case 'campaign':
      stickerType.value = 'campaign';
      break;
    case 'Campaign':
      stickerType.value = 'campaign';
      break;
    case 'Sweepstakes':
      stickerType.value = 'sweepstakes';
      break;
    case null:
      stickerType.value = 'default';
      break;
    default: stickerType.value = 'default';
  }
};

const isLoading = ref(false);

const fetchSticker = async () => {
  isLoading.value = true;
  const fetchedItem = await getSticker(route.params.id);
  await populateItem(fetchedItem);
  await store.dispatch('sticker/SET_STICKER', fetchedItem);
  if (fetchedItem.id) {
    // Use slug if available, fallback to ID
    const urlIdentifier = fetchedItem.slug || fetchedItem.id;
    stickerUrl.value = `${import.meta.env.VITE_WEB_BASE_URL}/web/sticker/${urlIdentifier}`;
  }
  isLoading.value = false;
};

// Add this function to update URL when slug changes
const updateStickerUrl = () => {
  if (item.id) {
    const urlIdentifier = item.slug || item.id;
    stickerUrl.value = `${import.meta.env.VITE_WEB_BASE_URL}/web/sticker/${urlIdentifier}`;
  }
};

// Watch for slug changes and update URL
watch(() => item.slug, () => {
  updateStickerUrl();
});

const getStickerInsights = async () => {
  const mStickerInsights = await findStickerInsights(route.params.id);
  if (mStickerInsights.unitsSold > 0) {
    isStickerSold.value = true;
  }
};

const updateAdminOrOrgAdminSticker = async (sticker: any) => {
  // Remove organization validation requirement - organization is optional
  // Only validate organization if user is org admin and we're not in edit mode
  if (!isEditPage.value && isOrgAdmin.value && !selectedOrganization.value) {
    errors.value = errors.value.filter((error) => error !== 'Organization');
    errors.value.push('Organization');

    setTimeout(() => {
      const errorElement = document.querySelector('.alert.alert-danger');
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
    return null;
  }

  // If organization is selected, attach the sticker to that organization
  if (selectedOrganization.value) {
    return await updateOrgSticker(selectedOrganization.value.id, sticker);
  }

  // If no organization is selected, create as Spotlight sticker (admin only)
  console.log('-- Creating Spotlight sticker (no organization selected)');
  return await updateSticker(sticker);
};

const checkErrorsAndGenerateMessages = () => {
  // shared on all
  if (!item.title.length) errors.value.push('Title');
  if (!item.url.length) errors.value.push('Image');

  const descriptionContent = item.description || '';
  if (descriptionContent.replace(/<[^>]*>/g, '').trim() === '') {
    errors.value.push('Details');
  }

  if (stickerType.value === 'default' || stickerType.value === 'campaign' || stickerType.value === 'ticket') {
    if (!item.tags.length) errors.value.push('Tags');
  }

  if (stickerType.value === 'campaign' || stickerType.value === 'ticket') {
    if (!item.startDateTimeZone) errors.value.push('Timezone');
  }

  // Add causes validation for campaign stickers
  if (stickerType.value === 'campaign') {
    if (!item.causes || !item.causes.length) errors.value.push('Non-Profit Suggestions');
  }

  // shared on campaign, ticket and sweepstakes
  if (stickerType.value === 'campaign' || stickerType.value === 'ticket' || stickerType.value === 'sweepstakes') {
    if (!item.startDate) errors.value.push('Start Date & Time');
    if (!item.endDate) errors.value.push('End Date & Time');
  }

  // only ticket has event date required
  if (stickerType.value === 'ticket') {
    if (!item.eventDate) errors.value.push('Event Date & Time');
    if (!item.varients?.length) errors.value.push('Tier is required');
  }

  // only for sweepstakes
  if (stickerType.value === 'sweepstakes') {
    if (!item.sweepstakesSticker.drawingDateTime) {
      errors.value.push('Drawing Date & Time');
    }

    const { entries } = item.sweepstakesSticker;
    const entryCosts = item.sweepstakesSticker.entryCost;

    if (!Array.isArray(entries) || entries.length === 0) {
      errors.value.push('Entries');
    } else {
      entries.forEach((entry) => {
        if (typeof entry !== 'number' || entry <= 0) {
          errors.value.push('Entries');
        }
      });
    }

    if (!Array.isArray(entryCosts) || entryCosts.length === 0) {
      errors.value.push('Entry Costs');
    } else {
      entryCosts.forEach((cost) => {
        if (typeof cost !== 'number' || cost < 0) {
          errors.value.push('Entry Costs');
        }
      });
    }
  }
};

const redirectToStickersPage = async () => {
  // If organization is selected, redirect to organization stickers page
  if (selectedOrganization.value) {
    if (isOrgAdmin.value) {
      await router.push(`/manage/orgs/${selectedOrganization.value.id}/stickers`);
      return;
    }
  }
  
  // If no organization is selected or user is admin, redirect to main stickers page
  await router.push('/manage-content/media/stickers');
};

const uploadDefaultSticker = async () => {
  checkErrorsAndGenerateMessages();
  if (errors.value.length) {
    window.scrollTo(0, 0);
    return;
  }

  const updatedItem = uploadDefaultStickerMap(item);

  try {
    await updateAdminOrOrgAdminSticker(updatedItem);
    await redirectToStickersPage();
  } catch (error) {
    throw error;
  }
};

const uploadCampaignSticker = async () => {
  checkErrorsAndGenerateMessages();
  if (errors.value.length) {
    window.scrollTo(0, 0);
    return;
  }
  const updatedItem = item;
  console.log('====== item: ',item)

  const cQuickCosts: number[] = [];
  const qc = item.quickCosts || [];
  qc.forEach((q) => {
    if (typeof q === 'number') {
      cQuickCosts.push(q);
    }
  });

  updatedItem.quickCosts = cQuickCosts;
  updatedItem.cost = parseFloat(parseFloat(updatedItem.cost).toFixed(2));
  if (Array.isArray(updatedItem.tags)) {
    updatedItem.tags = updatedItem.tags.join(' |=| ');
  } else if (typeof updatedItem.tags === 'string') {
    updatedItem.tags = updatedItem.tags.split(',').join(' |=| ');
  }

  if (Array.isArray(updatedItem.causes)) {
    updatedItem.causes = updatedItem.causes.join(' |=| ');
  } else if (typeof updatedItem.causes === 'string') {
    updatedItem.causes = updatedItem.causes.split(',').join(' |=| ');
  }

  item.campaignType = 'Campaign';
  updatedItem.varients = [];
  updatedItem.sweepstakesSticker = null;
  const stickerProps = getStickerProperties(updatedItem);
  console.log('-- uploadCampaignSticker stickerProps: ',stickerProps)
  try {
    await store.dispatch('sticker/SET_STICKER', stickerProps);
    await updateAdminOrOrgAdminSticker(stickerProps);
    await redirectToStickersPage();
  } catch (error) {
    throw error;
  }
};

const uploadTicketSticker = async () => {
  checkErrorsAndGenerateMessages();
  if (errors.value.length) {
    window.scrollTo(0, 0);
    return;
  }

  const updatedItem = uploadTicketStickerMap(item);
  if (updatedItem.varients?.length) {
    updatedItem.cost = 0;
  }
  try {
    await store.dispatch('sticker/SET_STICKER', updatedItem);
    await updateAdminOrOrgAdminSticker(updatedItem);
    await redirectToStickersPage();
  } catch (error) {
    throw error;
  }
};

const uploadSweepstakesSticker = async () => {
  checkErrorsAndGenerateMessages();
  if (errors.value.length) {
    window.scrollTo(0, 0);
    return;
  }

  const addItem = { ...item };
  addItem.cost = 0;
  addItem.tags = null;
  addItem.campaignType = 'Sweepstakes';
  addItem.isFeatured = false;
  addItem.IsCausesSearchAllowed = false;
  addItem.varients = [];

  if (addItem.sweepstakesSticker.numberOfPrizesAndWinners === null || addItem.sweepstakesSticker.numberOfPrizesAndWinners === '') {
    addItem.sweepstakesSticker.numberOfPrizesAndWinners = 1;
  }

  if (Array.isArray(item.causes)) {
    addItem.causes = item.causes.join(' |=| ');
  } else if (typeof item.causes === 'string') {
    addItem.causes = item.causes.split(',').join(' |=| ');
  } else {
    addItem.causes = '';
  }

  addItem.sweepstakesSticker.nonProfitBeneficiary = addItem.causes;

  const updatedItem = getSweepstakesStickerProperties(addItem);

  try {
    await store.dispatch('sticker/SET_STICKER', updatedItem);
    await updateAdminOrOrgAdminSticker(updatedItem);
    await redirectToStickersPage();
  } catch (error) {
    throw error;
  }
};

const onUpload = async () => {
  errors.value = [];
  console.log('-- stickerType.value: ',stickerType.value)
  if (stickerType.value === 'default') {
    await uploadDefaultSticker();
  } else if (stickerType.value === 'campaign') {
    await uploadCampaignSticker();
  } else if (stickerType.value === 'ticket') {
    await uploadTicketSticker();
  } else if (stickerType.value === 'sweepstakes') {
    await uploadSweepstakesSticker();
  }
};

const onDraw = async () => {
  errors.value = [];
  const sweepstakesStickerId = item.id;
  const participantIds = selectedWinners.value.map((winner) => winner.entityId);

  if (participantIds.length > 0) {
    try {
      return await drawWinner(sweepstakesStickerId, participantIds);
    } catch (error) {
      console.error('Error drawing winners:', error);
      ElMessage.error({
        message: 'Failed to draw winners. Please try again.',
        duration: 3000,
      });
    }
  } else {
    ElMessage.warning({
      message: 'Please select at least one participant to draw a winner.',
      duration: 3000,
    });
  }
  return null;
};

const getWinnersList = async () => {
  try {
    const sweepstakesStickerId = item.id;
    const response = await getWinners(sweepstakesStickerId);
    winners.value = response;
    if (winners.value) {
      winnerName.value = winners.value.map((winner) => winner.participantName).join(', ');
    } else {
      winnerName.value = '';
    }
  } catch (error) {
    console.error('Error fetching winners:', error);
  }
};

function confirmWinners() {
  Swal.fire({
    title: '<h3 style="font-size: 18px;">Are you sure?</h3>',
    html: '<p style="font-size: 14px;">Do you want to confirm winners?</p>',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#1EBEAF',
    cancelButtonColor: '#f43838',
    confirmButtonText: 'Yes',
    cancelButtonText: 'Cancel',
  }).then(async (result) => {
    if (result.isConfirmed) {
      try {
        await sendLoserEmails(item.id);
        window.location.reload();
      } catch (error) {
        console.error('Error sending loser emails:', error);
      }
    } else {
      console.log('Action cancelled');
    }
  });
}

const handleButtonClick = async () => {
  clickCount.value += 1;

  if (clickCount.value === 1) {
    isTextVisible.value = true;
    await getWinnersList();
  }

  if (clickCount.value === 2) {
    await onDraw();
    isTextVisible.value = false;
    clickCount.value = 0;
  }
};

const handleClose = async () => {
  isTextVisible.value = false;
  clickCount.value = 0;
  await getWinnersList();
};

const onAddImage = async (payload: any) => {
  try {
    if (payload.file) {
      const compressedFile = await resizeAndConvertToPNG(payload.file);

      const compressedPayload = {
        ...payload,
        file: compressedFile,
      };

      await handleUploadRequest(compressedPayload);

      if (uploadStatus.status === 'ok') {
        item.url = uploadStatus.resourceUrl;

        if (uploadStatus.thumbnailUrl) {
          item.thumbnailUrl = uploadStatus.thumbnailUrl;
        }
      }
    } else {
      await handleUploadRequest(payload);
      if (uploadStatus.status === 'ok') {
        item.url = uploadStatus.resourceUrl;
        if (uploadStatus.thumbnailUrl) {
          item.thumbnailUrl = uploadStatus.thumbnailUrl;
        }
      }
    }
  } catch (error) {
    console.error('Error processing image:', error);
    await handleUploadRequest(payload);
    if (uploadStatus.status === 'ok') {
      item.url = uploadStatus.resourceUrl;
      if (uploadStatus.thumbnailUrl) {
        item.thumbnailUrl = uploadStatus.thumbnailUrl;
      }
    }
  }
};

const tagsList = ref<TagData[]>([]);
const tagsArray = ref<string[]>([]);
async function getBullhornsAsTagsList() {
  const bullhornTags = await getBullhornsAsTags();

  bullhornTags.forEach((element: any) => {
    const data: TagData = { value: `${element.bullhorn}`, label: `${element.bullhorn}` };
    tagsList.value.push(data);
    // tagsArray is used to render the correct values for the new dropdown
    tagsArray.value.push(element.bullhorn);
  });
}

const causeList = ref<CauseData[]>([]);
const causeOptionsArray = ref<string[]>([]);
const causeLoading = ref(false);

async function getCausesList(query: any) {
  causeLoading.value = true;
  const benevityCauses = await getcausesAsDropdown(query);
  causeLoading.value = false;

  benevityCauses.forEach((element: any) => {
    const data = {
      value: `${element.name} : ${element.id}`,
      label: `${element.name} : ${element.id}`,
    };

    if (!causeOptionsArray.value.includes(data.value)) {
      causeList.value.push(data);
      causeOptionsArray.value.push(data.value);
    }
  });
}

const decideIfEditAndFetch = async () => {
  if (route.params?.id) {
    isEditPage.value = true;
    await fetchSticker();
  }
};

const isActive = () => {
  if (item.status === 'Active') {
    return true;
  }
  return false;
};

const handleClick = async () => {
  if (isUploading.value) return; // Prevent double clicks

  isUploading.value = true;

  try {
    if (isOrgAdmin.value && selectedOrganization.value) {
      if ((stickerType.value === 'default' && item.cost === 0) || stickerType.value === 'campaign') {
        // console.log("Skipping Stripe logic for 'default' stickerType with cost 0");
      } else {
        const organization = await getOrganization(selectedOrganization.value.id);
        isStripeOnboarded.value = organization.stripe_onboarded;
        if (!isStripeOnboarded.value) {
          ElMessage.error({
            message: 'You need to onboard with Stripe before uploading.',
            duration: 3000,
          });
          return;
        }
      }
    }

    if (stickerType.value === 'sweepstakes' && item.status === 'Active' && isEditPage.value) {
      await onDraw();
      await getWinnersList();
    } else {
      await onUpload();
    }
  } catch (error) {
    console.error('Error during upload:', error);
    let errorMessage = 'An error occurred during upload. Please try again.';
    if (error && error.errors && error.errors[0] && error.errors[0].message) {
      errorMessage = error.errors[0].message;
    } else if (error && error.message) {
      errorMessage = error.message;
    }
    ElMessage.error({
      message: errorMessage,
      duration: 3000,
    });
  } finally {
    isUploading.value = false;
  }
};

onMounted(async () => {
  await store.dispatch('sticker/RESET_TO_DEFAULT'); // reset so the state does not persist from before
  await decideIfEditAndFetch();
  await getBullhornsAsTagsList();
  await getCausesList('');
  if (isEditPage.value) {
    await getStickerInsights();
    if (isStickerSold.value) {
      await getWinnersList();
    }
  }
});
</script>

<template>
  <article class="c-add-sticker">
    <BreadcrumbActions
      :is-edit-page="isEditPage"
      :sticker-type="stickerType"
      :isActive="isActive()"
      :is-admin="isAdmin"
      :fetch-sticker="fetchSticker"
      :key="isEditPage.toString()"
    />
    <div v-if="isLoading" class="c-loading-spinner" />

    <section class="c-add-sticker__container" v-else>
      <section class="c-add-sticker__content">
        <article class="c-add-sticker__content__left">
          <StickerContent :onAddImage="onAddImage" :url="item.url" />
          <section class="c-add-sticker__qr-code" v-if="isEditPage && item && item.id">
            <b>Share Sticker</b>
            <qrcode-vue :value="stickerUrl" :size="167" />
            <a :href="stickerUrl" target="_blank" rel="noopener noreferrer">{{ stickerUrl }}</a>
          </section>

          <section v-if="errors.length" class="alert alert-danger fs-6 mt-3" role="alert">
            Please input required fields: 
            <b v-for="(error, index) in errors" :key="index">
              {{ error }}{{ index < errors.length - 1 ? ', ' : '' }}
            </b>
          </section>
        </article>

        <article class="c-add-sticker__content__right">
          <DefaultSticker v-if="stickerType === 'default'" :availableTags="tagsArray" />
          <CampaignSticker 
            v-if="stickerType === 'campaign'" 
            :availableTags="tagsArray" 
            :causes="causeOptionsArray" 
            @onFetchCauses="getCausesList" 
            :cause-loading="causeLoading" 
          />
          <TicketSticker 
            v-if="stickerType === 'ticket'" 
            :availableTags="tagsArray" 
            :causes="causeOptionsArray" 
            @onFetchCauses="getCausesList" 
            :cause-loading="causeLoading" 
          />
          <template v-if="stickerType === 'sweepstakes'">
            <SweepstakesSticker
              :class="{ 'disable-and-opacity': item.status === 'Active' }"
              :item="item"
              :disabled="isSweepstakeDisabled"
              :causes="causeOptionsArray"
              @onFetchCauses="getCausesList"
              :cause-loading="causeLoading"
            />
          </template>

          <article 
            v-if="stickerType === 'sweepstakes'" 
            class="s-sweepstakes-sticker__row s-sweepstakes-sticker__row--column" 
            style="margin-top: 10px;"
          >
            <p>
              By uploading you agree to our
              <a 
                href="https://www.spotlight.xyz/legal-stuff/sweepstakes-rules#age-restrictions" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                Age Restrictions
              </a>,
              <a 
                href="https://www.spotlight.xyz/legal-stuff/sweepstakes-rules#eligible-countries" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                Eligible Countries
              </a>,
              <a 
                href="https://www.spotlight.xyz/legal-stuff/sweepstakes-rules#donation-transparency" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                Donation Transparency
              </a>,
              <a 
                href="https://www.spotlight.xyz/legal-stuff/sweepstakes-rules#selection-process" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                Selection Process
              </a>, and
              <a 
                href="https://www.spotlight.xyz/legal-stuff/sweepstakes-rules#disclaimer" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                Disclaimer
              </a>.
            </p>
          </article>

          <!-- UPLOAD TO SPOTLIGHT CONTAINER -->
          <div class="" v-if="!isEditPage">
            <el-button
              class="c-add-sticker__content__submit"
              type="success"
              :loading="isUploading"
              :disabled="isUploading"
              @click="handleClick">
              <fai class="size-14" icon="upload" type="fas" v-if="!isUploading" />
              {{ isUploading ? 'Uploading...' : 'Upload to Spotlight' }}
            </el-button>
          </div>

          <!-- SAVE STICKER OR REDRAW BUTTON -->
          <div v-else>
            <!-- Save Sticker Button (only shown if sticker is not active and has no winners) -->
            <el-button
              v-if="stickerType !== 'sweepstakes' || 
                   (stickerType === 'sweepstakes' && item.status !== 'Active' && !isStickerSold)"
              class="c-add-sticker__content__submit"
              type="success"
              @click="handleClick">
              <fai class="size-14" icon="floppy-disk" type="fas" />
              Save Sticker
            </el-button>

            <!-- Redraw Button (shown if sticker is active and has winners, or if archived and has winners) -->
            <div style="display: flex; align-items: center; gap: 20px;">
              <div
                v-if="(stickerType === 'sweepstakes' && 
                      (item.status === 'Active' || item.status === 'Archived') && 
                      isStickerSold && !item.isWinnerConfirmed)"
                class="redraw-container"
                style="position: relative; width: 100%;">
                <el-button
                  class="c-add-sticker__content__submit"
                  style="background-color: #015C6B !important; border-color: #015C6B;"
                  type="success"
                  :disabled="winners === null"
                  @click="handleButtonClick">
                  <fai class="size-14" icon="repeat" type="fas" />
                  Redraw Winners
                </el-button>
                <!-- Winner list appears above the button when clicked -->
                <div
                  v-if="isTextVisible"
                  class="c-add-sticker__winners-list"
                  style="position: absolute; bottom: 100%; left: 0; width: 345px; background: white; 
                         box-shadow: rgba(0, 0, 0, 0.3) 0px 4px 20px; padding: 30px; z-index: 10; 
                         max-height: 300px; overflow-y: auto;">
                  <button type="button" class="close-btn" @click="handleClose">X</button>
                  <p style="font-weight: bold;">Choose who to redraw for:</p>
                  <ul style="list-style: none; padding-left: 0;">
                    <li 
                      v-for="winner in winners" 
                      :key="winner.id" 
                      class="winner-item" 
                      style="display: flex; align-items: center; justify-content: space-between;"
                    >
                      <div style="display: flex; align-items: center;">
                        <img
                          v-if="winner.participantAvatar"
                          :src="winner.participantAvatar"
                          alt="Winner Avatar"
                          style="width: 20px; height: 20px; margin-right: 8px; 
                                 border-radius: 50%;" />
                        <fai
                          v-else
                          class="icon-size-14"
                          type="fas"
                          icon="user-circle"
                          style="width: 20px; height: 20px; margin-right: 8px;" />
                        <label :for="winner.id">{{ winner.participantName }}</label>
                      </div>
                      <el-checkbox
                        v-model="selectedWinners"
                        :id="winner.id"
                        :value="winner"
                        class="c-quick-costs" />
                    </li>
                  </ul>
                </div>
              </div>

              <el-button
                v-if="stickerType === 'sweepstakes' && 
                     (item.status === 'Active' || item.status === 'Archived') && 
                     isStickerSold && !item.isWinnerConfirmed"
                class="c-add-sticker__content__submit"
                type="success"
                @click="confirmWinners"
                :disabled="winners === null">
                Confirm Winners
              </el-button>
            </div>
          </div>

        </article>
      </section>

      <section 
        class="c-add-sticker__extra" 
        :class="{ 'disabled-section': stickerType === 'sweepstakes' && item.status === 'Active' }"
      >
        <article class="c-add-sticker__extra__type">
          <span class="c-add-sticker__extra__title mb-1">Sticker Type</span>
          <el-select
            v-model="stickerType"
            class="c-input c-input__select"
            :disabled="stickerType === 'sweepstakes' && item.status === 'Active'"
          >
            <el-option 
              v-if="!(isEditPage && stickerType === 'sweepstakes')" 
              label="Default" 
              value="default"
            >
              Default
            </el-option>
            <el-option 
              v-if="!(isEditPage && stickerType === 'sweepstakes')" 
              label="Campaign" 
              value="campaign"
            >
              Campaign
            </el-option>
            <el-option 
              v-if="!(isEditPage && stickerType === 'sweepstakes')" 
              label="Ticket" 
              value="ticket"
            >
              Ticket
            </el-option>
            <el-option 
              v-if="!(isEditPage && stickerType !== 'sweepstakes')" 
              label="Sweepstakes" 
              value="sweepstakes"
            >
              Sweepstakes
            </el-option>
          </el-select>
        </article>
        <Insights class="c-add-sticker__extra__type" v-if="isEditPage" />
      </section>
    </section>
  </article>
</template>

<style lang="scss" src="./AddSticker.scss" />
